import { useState, useContext, useEffect } from "react";
import { Form, Button, Card } from "react-bootstrap";
import {
    PersonBadge, CarFront, ClipboardCheck, Trash,
    Save, XCircle, ArrowRight, PinMap, GeoAlt, Hash
} from "react-bootstrap-icons";
import { InfraccionesContext } from "../context/InfraccionesContext";
import "./infractor.css";
import municipiosYColonias from "../data/municipios.json";

const estadosRepublica = [
    "Aguascalientes",
    "Baja California",
    "Baja California Sur",
    "Campeche",
    "Chiapas",
    "Chihuahua",
    "Ciudad de México",
    "Coahuila",
    "Colima",
    "Durango",
    "Guanajuato",
    "Guerrero",
    "Hidalgo",
    "Jalisco",
    "México",
    "Michoacán",
    "Morelos",
    "Nayarit",
    "Nuevo León",
    "Oaxaca",
    "Puebla",
    "Querétaro",
    "Quintana Roo",
    "San Luis Potosí",
    "Sinaloa",
    "Sonora",
    "Tabasco",
    "Tamaulipas",
    "Tlaxcala",
    "Veracruz",
    "Yucatán",
    "Zacatecas",
];

export default function InfractorVehiculo({ setActivePage }) {
    const { form, setForm, resetForm } = useContext(InfraccionesContext);
    const [licenciaExpirada, setLicenciaExpirada] = useState(false);

    useEffect(() => {
        setForm((prevForm) => {
            const nextEstado = prevForm.estadoInfractor || "México";
            const nextExpedido = prevForm.expedidoEn || "México";
            const nextMunicipio = !prevForm.conductorAusente && !prevForm.municipioInfractor
                ? "IXTAPALUCA"
                : prevForm.municipioInfractor;

            if (
                nextEstado === prevForm.estadoInfractor &&
                nextExpedido === prevForm.expedidoEn &&
                nextMunicipio === prevForm.municipioInfractor
            ) {
                return prevForm;
            }

            return {
                ...prevForm,
                estadoInfractor: nextEstado,
                expedidoEn: nextExpedido,
                municipioInfractor: nextMunicipio,
            };
        });
    }, [setForm]);

    useEffect(() => {
        if (form.emision && form.vigencia && form.vigencia !== "Permanente") {
            const fechaEmision = new Date(form.emision);
            const fechaVencimiento = new Date(fechaEmision.setFullYear(fechaEmision.getFullYear() + parseInt(form.vigencia)));
            setLicenciaExpirada(new Date() > fechaVencimiento);
        } else {
            setLicenciaExpirada(false);
        }
    }, [form.emision, form.vigencia]);

    // Validaciones por campo
    const validaciones = {
        nombre: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        apellidoPaterno: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        apellidoMaterno: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        estadoInfractor: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        expedidoEn: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        marca: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        submarca: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        color: value => value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''),
        // Para agregar nuevos campos: 'nombreCampo': value => value.replace(/regex/g, '')
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Checkbox
        if (type === "checkbox") {
            if (name === "sinLicencia") {
                setForm({
                    ...form,
                    sinLicencia: checked,
                    licencia: checked ? "SIN LICENCIA" : "",
                    emision: checked ? "" : form.emision,
                    vigencia: checked ? "" : form.vigencia,
                    tipoLicencia: checked ? "" : form.tipoLicencia,
                    estadoInfractor: checked ? "" : "México",
                });
            } else {
                setForm({ ...form, [name]: checked });
            }
            return;
        }

        // Aplicar validación si existe
        let nuevoValor = value;
        if (validaciones[name]) {
            nuevoValor = validaciones[name](value);
        }

        // Actualizar estado del formulario y limpiar colonias si cambia el municipio
        setForm({
            ...form,
            [name]: nuevoValor,
            ...(name === "municipioInfractor" ? { coloniaInfractor: "" } : {})
        });
    };


    const handleClear = () => {
        if (window.confirm("¿Estás seguro de que deseas limpiar todos los campos?")) {
            resetForm();
        }
    };

    const handleNext = () => {
        setActivePage("fotografias");
    };

    const handleSave = () => {
        alert("Datos guardados.");
        setForm(form);
    };

    const handleCancel = () => {
        if (window.confirm("¿Estás seguro de que deseas cancelar y perder los cambios?")) {
            resetForm();
            setActivePage("infracciones");
        }
    };

    return (
        <div className="infractor-page">
            <div className="container">
            <h1 className="title">Infractor / Vehículo</h1>
            <div className="divider"></div>

            <Card className="info-card mb-6">
                <p>Los campos con asterisco (*) son obligatorios</p>
            </Card>

            <Form className="form-container">
                <Card className="mb-6 p-4">
                    <Form.Check
                        type="checkbox"
                        label="Conductor ausente"
                        name="conductorAusente"
                        checked={form.conductorAusente}
                        onChange={handleChange}
                    />

                    <h3>Datos del Conductor</h3>
                    <div className="form-section">
                        {[
                            { label: "Nombre *", name: "nombre", icon: <PersonBadge />, placeholder: "Nombre del infractor" },
                            { label: "Apellido Paterno *", name: "apellidoPaterno", icon: <PersonBadge />, placeholder: "Apellido Paterno" },
                            { label: "Apellido Materno *", name: "apellidoMaterno", icon: <PersonBadge />, placeholder: "Apellido Materno" },
                            { label: "RFC", name: "rfc", icon: <Hash />, placeholder: "RFC (Opcional)", maxLength: 13, pattern: "[A-Za-z0-9]{13}" },
                            { label: "Calle *", name: "calleInfractor", icon: <PinMap />, placeholder: "Calle" },
                            { label: "Número Exterior", name: "exterior", icon: <PinMap />, placeholder: "Número Exterior", pattern: "\\d{1,3}", maxLength: 3 },
                            { label: "Número Interior", name: "interior", icon: <PinMap />, placeholder: "Número Interior", pattern: "\\d{1,3}", maxLength: 3 },
                            { label: "Código Postal", name: "codigoPostal", icon: <GeoAlt />, placeholder: "Código Postal", pattern: "\\d{5}", maxLength: 5 }
                        ].map((field, index) => (
                            <div key={index} className="form-group">
                                <label className="form-label">{field.label}</label>
                                <div className="input-group">
                                    {field.icon && <span className="input-icon">{field.icon}</span>}
                                    <Form.Control
                                        type={field.type || "text"}
                                        name={field.name}
                                        value={form[field.name] ?? field.defaultValue ?? ""}
                                        onChange={handleChange}
                                        required={field.label.includes("*")}
                                        placeholder={field.placeholder}
                                        maxLength={field.maxLength}
                                        pattern={field.pattern}
                                        disabled={form.conductorAusente}
                                        className={form.conductorAusente ? "conductor-disabled" : ""}
                                    />
                                </div>
                            </div>
                        ))}

                        <Form.Group>
                            <Form.Label>Municipio *</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><PinMap /></span>
                                <Form.Control as="select" name="municipioInfractor" value={form.municipioInfractor} onChange={handleChange} required disabled={form.conductorAusente} className={form.conductorAusente ? "conductor-disabled" : ""}>
                                    <option value="">Seleccione un municipio</option>
                                    {Object.keys(municipiosYColonias).map((municipio, index) => (
                                        <option key={index} value={municipio}>{municipio}</option>
                                    ))}
                                </Form.Control>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Colonia *</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><PinMap /></span>
                                <Form.Control as="select" name="coloniaInfractor" value={form.coloniaInfractor} onChange={handleChange} required disabled={!form.municipioInfractor || form.conductorAusente} className={form.conductorAusente ? "conductor-disabled" : ""}>
                                    <option value="">Seleccione una colonia</option>
                                    {form.municipioInfractor && municipiosYColonias[form.municipioInfractor].map((colonia, index) => (
                                        <option key={index} value={colonia}>{colonia}</option>
                                    ))}
                                </Form.Control>
                            </div>
                        </Form.Group>
                    </div>

                    <h3>Licencia del Conductor</h3>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Sin Licencia"
                            name="sinLicencia"
                            checked={form.sinLicencia || false}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <div className="form-section">
                        <Form.Group>
                            <Form.Label>Licencia</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><ClipboardCheck /></span>
                                <Form.Control
                                    type="text"
                                    name="licencia"
                                    value={form.licencia ?? ""}
                                    onChange={handleChange}
                                    placeholder="Licencia"
                                    disabled={form.conductorAusente || form.sinLicencia}
                                    className={(form.conductorAusente || form.sinLicencia) ? "conductor-disabled" : ""}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Fecha de Emisión {!(form.conductorAusente || form.sinLicencia) && "*"}</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><ClipboardCheck /></span>
                                <Form.Control
                                    type="date"
                                    name="emision"
                                    value={form.emision ?? ""}
                                    onChange={handleChange}
                                    required={!form.conductorAusente && !form.sinLicencia}
                                    disabled={form.conductorAusente || form.sinLicencia}
                                    className={(form.conductorAusente || form.sinLicencia) ? "conductor-disabled" : ""}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Estado {!(form.conductorAusente || form.sinLicencia) && "*"}</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><GeoAlt /></span>
                                <Form.Select
                                    name="estadoInfractor"
                                    value={form.estadoInfractor}
                                    onChange={handleChange}
                                    required={!form.conductorAusente && !form.sinLicencia}
                                    disabled={form.conductorAusente || form.sinLicencia}
                                    className={(form.conductorAusente || form.sinLicencia) ? "conductor-disabled" : ""}
                                >
                                    <option value="">Seleccione un estado</option>
                                    {estadosRepublica.map((estado) => (
                                        <option key={estado} value={estado}>{estado}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Tipo de Licencia {!(form.conductorAusente || form.sinLicencia) && "*"}</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><ClipboardCheck /></span>
                                <Form.Select
                                    name="tipoLicencia"
                                    value={form.tipoLicencia}
                                    onChange={handleChange}
                                    disabled={form.conductorAusente || form.sinLicencia}
                                    className={(form.conductorAusente || form.sinLicencia) ? "conductor-disabled" : ""}
                                >
                                    <option value="">Seleccione un tipo de licencia</option>
                                    <option value="A">Tipo A</option>
                                    <option value="C">Tipo C</option>
                                    <option value="E">Tipo E</option>
                                    <option value="D">Tipo D</option>
                                </Form.Select>
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Años de Vigencia {!(form.conductorAusente || form.sinLicencia) && "*"}</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><ClipboardCheck /></span>
                                <Form.Select
                                    name="vigencia"
                                    value={form.vigencia}
                                    onChange={handleChange}
                                    disabled={form.conductorAusente || form.sinLicencia}
                                    className={(form.conductorAusente || form.sinLicencia) ? "conductor-disabled" : ""}
                                >
                                    <option value="">Seleccione vigencia</option>
                                    {[1, 2, 3, 4, 5].map((a) => (
                                        <option key={a} value={a}>{a} año(s)</option>
                                    ))}
                                    <option value="Permanente">Permanente</option>
                                </Form.Select>
                            </div>
                            {!form.sinLicencia && licenciaExpirada && (
                                <small className="text-danger">Licencia expirada, actualice motivos de la infracción</small>
                            )}
                        </Form.Group>
                    </div>
                </Card>

                <Card className="mb-6 p-4">
                    <h3>Datos del Vehículo</h3>
                    <div className="form-section">
                        {[
                            { label: "Placas *", name: "placas", icon: <CarFront />, placeholder: "Placas del vehículo" },
                            { label: "Serie del Auto *", name: "serieAuto", icon: <CarFront />, placeholder: "Serie del auto" }
                        ].map((field, index) => (
                            <div key={index} className="form-group">
                                <label className="form-label">{field.label}</label>
                                <div className="input-group">
                                    {field.icon && <span className="input-icon">{field.icon}</span>}
                                    <Form.Control
                                        type="text"
                                        name={field.name}
                                        value={form[field.name]}
                                        onChange={handleChange}
                                        required={field.label.includes("*")}
                                        placeholder={field.placeholder}
                                        pattern={field.pattern}
                                    />
                                </div>
                            </div>
                        ))}

                        <Form.Group>
                            <Form.Label>Expedido en *</Form.Label>
                            <div className="input-group">
                                <span className="input-icon"><CarFront /></span>
                                <Form.Select
                                    name="expedidoEn"
                                    value={form.expedidoEn || "México"}
                                    onChange={handleChange}
                                    required
                                >
                                    {estadosRepublica.map((estado) => (
                                        <option key={estado} value={estado}>{estado}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </Form.Group>

                        {[
                            { label: "Marca *", name: "marca", icon: <CarFront />, placeholder: "Marca del vehículo" },
                            { label: "Submarca *", name: "submarca", icon: <CarFront />, placeholder: "Submarca del vehículo" },
                            { label: "Color *", name: "color", icon: <CarFront />, placeholder: "Color del vehículo" },
                            { label: "Modelo *", name: "modelo", icon: <CarFront />, placeholder: "Modelo del vehículo", pattern: "\\d{4}", maxLength: 4 },
                            { label: "Tarjeta de Circulación", name: "tarjetaCirculacion", icon: <CarFront />, placeholder: "Tarjeta de circulación" },
                            { label: "Folio de Inventario", name: "folioInventario", icon: <CarFront />, placeholder: "Folio de inventario" },
                            { label: "Depósito", name: "deposito", icon: <CarFront />, placeholder: "Depósito" }
                        ].map((field, index) => (
                            <div key={index} className="form-group">
                                <label className="form-label">{field.label}</label>
                                <div className="input-group">
                                    {field.icon && <span className="input-icon">{field.icon}</span>}
                                    <Form.Control
                                        type="text"
                                        name={field.name}
                                        value={form[field.name]}
                                        onChange={handleChange}
                                        required={field.label.includes("*")}
                                        placeholder={field.placeholder}
                                        pattern={field.pattern}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <div className="button-group">
                    <Button type="button" variant="primary" onClick={handleSave}><Save /> Guardar</Button>
                    <Button type="button" variant="success" onClick={handleNext}><ArrowRight /> Siguiente</Button>
                    <Button type="button" variant="secondary" onClick={handleClear}><Trash /> Limpiar</Button>
                    <Button type="button" variant="danger" onClick={handleCancel}><XCircle /> Cancelar</Button>
                </div>
            </Form>
            </div>
        </div>
    );
}
